/**
 * The Frakt queue count (P25009-450 BR-6): how many of this seller's shipping
 * orders are waiting for a freight booking.
 *
 * A shared duck rather than FreightPage state because the number lights up the
 * "Frakt" badge in SellerNav, which renders on every seller page — the queue
 * is work with a deadline (purchased orders auto-cancel after six days), and a
 * count nobody sees until they open the page is exactly the buried-queue
 * failure the rail row exists to fix.
 *
 * The queue definition matches FreightPage's: own sales in `purchased` whose
 * protectedData.deliveryMethod is 'shipping'. Delivery method is not a
 * queryable transaction field, so the fetch pulls the purchased page and
 * filters here — purchased orders are the seller's live to-do list and are
 * few, so one page of 100 covers it (a seller with more has bigger problems
 * than a badge reading 99+).
 *
 * SellerNav triggers the fetch; a staleness window keeps that from becoming
 * one query per navigation.
 */

import { storableError } from '../util/errors';

// ================ Action types ================ //

export const FETCH_COUNT_REQUEST = 'app/freightQueue/FETCH_COUNT_REQUEST';
export const FETCH_COUNT_SUCCESS = 'app/freightQueue/FETCH_COUNT_SUCCESS';
export const FETCH_COUNT_ERROR = 'app/freightQueue/FETCH_COUNT_ERROR';

// ================ Reducer ================ //

const initialState = {
  count: 0,
  fetchedAt: null,
  fetchInProgress: false,
  fetchError: null,
};

export default function freightQueueReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_COUNT_REQUEST:
      return { ...state, fetchInProgress: true, fetchError: null };
    case FETCH_COUNT_SUCCESS:
      return { ...state, fetchInProgress: false, count: payload.count, fetchedAt: payload.fetchedAt };
    case FETCH_COUNT_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };
    default:
      return state;
  }
}

// ================ Thunks ================ //

/* How long a fetched count stays good enough for a badge. The page itself
 * always shows live data; the badge may lag this far behind an action taken
 * in another tab. */
const STALE_AFTER_MS = 60 * 1000;

export const fetchFreightQueueCount = ({ force = false } = {}) => (dispatch, getState, sdk) => {
  const { fetchInProgress, fetchedAt } = getState().freightQueue;
  const fresh = fetchedAt && Date.now() - fetchedAt < STALE_AFTER_MS;
  if (fetchInProgress || (fresh && !force)) {
    return Promise.resolve();
  }

  dispatch({ type: FETCH_COUNT_REQUEST });

  return sdk.transactions
    .query({
      only: 'sale',
      // The state/ prefix is load-bearing: without it the API silently
      // matches nothing (the same trap the order-list filter chips carry
      // their values as `state/<name>` to avoid).
      states: 'state/purchased',
      'fields.transaction': ['processName', 'lastTransition', 'protectedData'],
      perPage: 100,
    })
    .then(response => {
      const count = (response.data.data || []).filter(
        tx => tx.attributes?.protectedData?.deliveryMethod === 'shipping'
      ).length;
      dispatch({ type: FETCH_COUNT_SUCCESS, payload: { count, fetchedAt: Date.now() } });
      return count;
    })
    .catch(e => {
      // A failed badge fetch is not worth an error surface — the nav renders
      // without a count, the page still shows the real queue.
      dispatch({ type: FETCH_COUNT_ERROR, payload: storableError(e) });
      return null;
    });
};
